function initSongbondReviews(scope=document){
/* Reader-controlled pagination; no timers initiate carousel movement. */
scope.querySelectorAll('.sb-reviews-carousel').forEach((carousel, carouselIndex) => {
  if (carousel.hasAttribute('data-reviews-enhanced')) return;
  const rail = carousel.querySelector('.sb-reviews-rail');
  const cards = rail ? [...rail.querySelectorAll('.sb-review-card')] : [];
  const controls = carousel.querySelector('.sb-reviews-controls');
  const previous = carousel.querySelector('[data-review-previous]');
  const next = carousel.querySelector('[data-review-next]');
  const status = carousel.querySelector('.sb-reviews-status');
  if (!rail || !cards.length || !controls || !previous || !next || !status) return;

  rail.id ||= `customer-reviews-rail-${carouselIndex + 1}`;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const pagination = document.createElement('div');
  pagination.className = 'sb-reviews-pagination';
  pagination.setAttribute('role', 'group');
  pagination.setAttribute('aria-label', 'Review pages');
  const before = document.createElement('span');
  const after = document.createElement('span');
  [before, after].forEach(mark => {
    mark.className = 'sb-reviews-more';
    mark.textContent = '…';
    mark.setAttribute('aria-hidden', 'true');
  });
  controls.replaceChildren(previous, pagination, next, status);
  carousel.setAttribute('data-reviews-enhanced', '');
  carousel.setAttribute('aria-roledescription', 'carousel');
  previous.setAttribute('aria-controls', rail.id);
  next.setAttribute('aria-controls', rail.id);

  // Full quotes remain in the HTML; only enhanced mobile cards are shortened.
  const compactReviews = window.matchMedia('(max-width: 699px)');
  const reviewCopies = (carousel.closest('.sb-product-shared-reviews') ? cards : []).map((card, index) => {
    const quote = card.querySelector('blockquote');
    if (!quote) return null;
    quote.id ||= `${rail.id}-quote-${index + 1}`;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'sb-review-expand';
    button.hidden = true;
    button.setAttribute('aria-controls', quote.id);
    button.setAttribute('aria-expanded', 'false');
    button.textContent = carousel.dataset.reviewMoreLabel || 'Read full review';
    quote.after(button);
    const entry = {card, quote, button};
    button.addEventListener('click', () => setReviewExpanded(entry, !card.classList.contains('is-review-expanded')));
    return entry;
  }).filter(Boolean);

  function setReviewExpanded(entry, expanded) {
    entry.card.classList.toggle('is-review-expanded', expanded);
    entry.button.setAttribute('aria-expanded', String(expanded));
    entry.button.textContent = expanded
      ? (carousel.dataset.reviewLessLabel || 'Show less')
      : (carousel.dataset.reviewMoreLabel || 'Read full review');
  }

  function measureReviewText() {
    reviewCopies.forEach(entry => {
      const lineHeight = parseFloat(getComputedStyle(entry.quote).lineHeight);
      const collapsible = compactReviews.matches && entry.quote.scrollHeight > lineHeight * 5 + 1;
      entry.card.toggleAttribute('data-review-collapsible', collapsible);
      entry.button.hidden = !collapsible;
      if (!collapsible) setReviewExpanded(entry, false);
    });
  }

  let pages = [];
  let buttons = [];
  let activePage = 0;
  let requestedPage = null;
  let anchorCard = 0;
  let maxScroll = 0;
  let geometry = '';
  let scrollFrame = 0;
  let resizeFrame = 0;
  let settleTimer = 0;

  function pageLabel(page) {
    return page.first === page.last
      ? `Review ${page.first + 1} of ${cards.length}`
      : `Reviews ${page.first + 1}–${page.last + 1} of ${cards.length}`;
  }

  function paintIndicators() {
    if (!pages.length) return;
    const gap = parseFloat(getComputedStyle(controls).columnGap) || 0;
    // Reserve both 48px arrows and two small continuation marks.
    const capacity = Math.max(1, Math.min(5,
      Math.floor((controls.clientWidth - 96 - gap * 2 - 24) / 44)));
    const shown = Math.min(capacity, pages.length);
    const centre = requestedPage ?? activePage;
    const start = Math.max(0, Math.min(centre - Math.floor(shown / 2), pages.length - shown));
    buttons.forEach((button, index) => {
      button.hidden = index < start || index >= start + shown;
      if (index === activePage) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
    before.dataset.empty = String(start === 0);
    after.dataset.empty = String(start + shown === pages.length);
  }

  function update() {
    if (!pages.length) return;
    const position = Math.max(0, Math.min(rail.scrollLeft, maxScroll));
    activePage = pages.reduce((best, page, index) =>
      Math.abs(page.left - position) < Math.abs(pages[best].left - position) ? index : best, 0);
    const bounds = rail.getBoundingClientRect();
    const visible = cards.map((card, index) => ({index, bounds: card.getBoundingClientRect()}))
      .filter(({bounds: card}) => card.right > bounds.left + card.width / 2
        && card.left < bounds.right - card.width / 2);
    const visibleRange = {
      first: visible[0]?.index ?? pages[activePage].first,
      last: visible[visible.length - 1]?.index ?? pages[activePage].last
    };
    anchorCard = visibleRange.first;
    previous.disabled = position <= 2;
    next.disabled = position >= maxScroll - 2;
    const atPage = Math.abs(position - pages[activePage].left) <= 2;
    const label = `${pageLabel(visibleRange)}.${atPage ? ` Page ${activePage + 1} of ${pages.length}.` : ''}`;
    if (status.textContent !== label) status.textContent = label;
    paintIndicators();
  }

  function finishScroll() {
    clearTimeout(settleTimer);
    requestedPage = null;
    update();
  }

  function goTo(index, focusPage = false) {
    if (!pages.length) return;
    if (compactReviews.matches) reviewCopies.forEach(entry => setReviewExpanded(entry, false));
    requestedPage = Math.max(0, Math.min(index, pages.length - 1));
    paintIndicators();
    if (focusPage) buttons[requestedPage].focus({preventScroll: true});
    rail.scrollTo({left: pages[requestedPage].left, behavior: reducedMotion.matches ? 'auto' : 'smooth'});
    // Also settle when the requested page is already at the current position.
    clearTimeout(settleTimer);
    settleTimer = setTimeout(finishScroll, 180);
  }

  function measure() {
    measureReviewText();
    const width = rail.clientWidth;
    const cardWidth = cards[0].getBoundingClientRect().width;
    if (!width || !cardWidth) return;
    const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
    const signature = `${width}:${cardWidth}:${gap}:${cards.length}`;
    if (signature === geometry) { paintIndicators(); return; }
    geometry = signature;
    const hadPageFocus = pagination.contains(document.activeElement);
    const preservedCard = anchorCard;
    maxScroll = Math.max(0, rail.scrollWidth - width);
    const railLeft = rail.getBoundingClientRect().left;
    const positions = cards.map(card => card.getBoundingClientRect().left - railLeft + rail.scrollLeft);
    const perPage = Math.max(1, Math.floor((width + gap + 2) / (cardWidth + gap)));
    pages = [];
    for (let i = 0; i < cards.length; i += perPage) {
      const left = Math.max(0, Math.min(positions[i], maxScroll));
      if (pages.length && Math.abs(pages[pages.length - 1].left - left) < 2) continue;
      const visible = positions.map((position, index) => ({position, index}))
        .filter(({position}) => position + cardWidth / 2 > left && position + cardWidth / 2 < left + width);
      pages.push({left, first: visible[0]?.index ?? i, last: visible[visible.length - 1]?.index ?? i});
    }
    buttons = pages.map((page, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'sb-reviews-page';
      button.dataset.reviewPage = String(index);
      button.setAttribute('aria-controls', rail.id);
      button.setAttribute('aria-label', `Show ${pageLabel(page).toLowerCase()}`);
      button.addEventListener('click', () => goTo(index));
      return button;
    });
    pagination.replaceChildren(before, ...buttons, after);
    activePage = pages.reduce((best, page, index) =>
      Math.abs(page.first - preservedCard) < Math.abs(pages[best].first - preservedCard) ? index : best, 0);
    requestedPage = null;
    clearTimeout(settleTimer);
    rail.scrollTo({left: pages[activePage].left, behavior: 'auto'});
    update();
    if (hadPageFocus) buttons[activePage].focus({preventScroll: true});
  }

  previous.addEventListener('click', () => goTo((requestedPage ?? activePage) - 1));
  next.addEventListener('click', () => goTo((requestedPage ?? activePage) + 1));
  rail.addEventListener('keydown', event => {
    if (event.target !== rail || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const target = event.key === 'Home' ? 0 : event.key === 'End' ? pages.length - 1
      : (requestedPage ?? activePage) + (event.key === 'ArrowRight' ? 1 : -1);
    goTo(target);
  });
  pagination.addEventListener('keydown', event => {
    if (!event.target.matches('.sb-reviews-page') || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const target = event.key === 'Home' ? 0 : event.key === 'End' ? pages.length - 1
      : Number(event.target.dataset.reviewPage) + (event.key === 'ArrowRight' ? 1 : -1);
    goTo(target, true);
  });
  ['pointerdown', 'wheel'].forEach(type => rail.addEventListener(type, () => {
    requestedPage = null;
    clearTimeout(settleTimer);
  }, {passive: true}));
  rail.addEventListener('scroll', () => {
    if (!scrollFrame) scrollFrame = requestAnimationFrame(() => { scrollFrame = 0; update(); });
    clearTimeout(settleTimer);
    settleTimer = setTimeout(finishScroll, 180);
  }, {passive: true});
  rail.addEventListener('scrollend', finishScroll);
  const observer = new ResizeObserver(() => {
    if (!resizeFrame) resizeFrame = requestAnimationFrame(() => { resizeFrame = 0; measure(); });
  });
  observer.observe(rail);
  observer.observe(controls);
  measure();
  if (document.fonts?.ready) document.fonts.ready.then(measure);
});

}
initSongbondReviews();
document.addEventListener("shopify:section:load",event=>initSongbondReviews(event.target));
